/**
 * savedViews.ts — localStorage-backed saved filter views
 *
 * Provides typed CRUD helpers for saving/loading filter presets per page per user.
 * Storage: localStorage keyed by `litpulse_saved_views_{userId}_{page}`
 *
 * This is PRESENTATION state only — it does not change any API behavior,
 * source-of-truth logic, or entitlement enforcement.
 */

export interface SavedView {
  id: string;
  name: string;
  createdAt: string;
  filters: Record<string, string | null>;
}

type PageKey = 'lithub' | 'litscreen';

function storageKey(userId: string, page: PageKey): string {
  return `litpulse_saved_views_${userId}_${page}`;
}

function generateId(): string {
  return `sv_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * Get all saved views for a user + page.
 */
export function getSavedViews(userId: string, page: PageKey): SavedView[] {
  try {
    const raw = localStorage.getItem(storageKey(userId, page));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/**
 * Save a new view. Returns the created view.
 */
export function createSavedView(
  userId: string,
  page: PageKey,
  name: string,
  filters: Record<string, string | null>,
): SavedView {
  const views = getSavedViews(userId, page);
  const view: SavedView = {
    id: generateId(),
    name,
    createdAt: new Date().toISOString(),
    filters,
  };
  views.push(view);
  localStorage.setItem(storageKey(userId, page), JSON.stringify(views));
  return view;
}

/**
 * Rename a saved view.
 */
export function renameSavedView(
  userId: string,
  page: PageKey,
  viewId: string,
  newName: string,
): boolean {
  const views = getSavedViews(userId, page);
  const view = views.find(v => v.id === viewId);
  if (!view) return false;
  view.name = newName;
  localStorage.setItem(storageKey(userId, page), JSON.stringify(views));
  return true;
}

/**
 * Delete a saved view.
 */
export function deleteSavedView(
  userId: string,
  page: PageKey,
  viewId: string,
): boolean {
  const views = getSavedViews(userId, page);
  const filtered = views.filter(v => v.id !== viewId);
  if (filtered.length === views.length) return false;
  localStorage.setItem(storageKey(userId, page), JSON.stringify(filtered));
  return true;
}
